import { Form, Input, message } from "antd";
import { useForm } from "antd/es/form/Form";
import { AxiosResponse } from "axios";
import { useContext, useEffect, useState } from "react";
import CustomModal from "../../../commons/components/CustomModal/CustomModal";
import PrimaryButton from "../../../commons/components/PrimaryButton/PrimaryButton";
import { MERCHANT_ONBOARD_REQUEST_VERIFY, MERCHANT_ONBOARD_VERIFY } from "../../../commons/constants/api-urls";
import defaultAxios from "../../../commons/utils/axios";
import styles from "./verify-otp.module.scss";
import InputOtp from "../../../commons/components/CustomInput/OtpInput";
import { routers } from "../../../commons/constants/routers";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../contexts/AppContext";

interface VerifyOTPProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  name: string;
  merchantOnboardId: string;
  onSuccess: (activate_token: string) => void;
  email: string;
}
const VerifyOTP = ({ open, setOpen, name, merchantOnboardId, email, onSuccess }: VerifyOTPProps) => {
  const { clearUserData } = useContext(AppContext);
  const [form] = useForm<{ otp: string; newPassword: string; confirmPassword: string }>();
  const [hiddenEmail, setHiddenEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  const requestVerifyOTP = async (merchantOnboardId: string) => {
    try {
      const res: AxiosResponse<any> = await defaultAxios.get(MERCHANT_ONBOARD_REQUEST_VERIFY(merchantOnboardId));
      if (!!res?.data) {
        message.info("Request resend OTP successfully");
      }
    } catch (error: any) {
      message.error(error.response.data.error.message);
    }
  };
  const onFinish = async (values: { otp: string; newPassword: string; confirmPassword: string }) => {
    setLoading(true);

    if (!merchantOnboardId) {
      setLoading(false);
      message.error("merchant onboard request not found!");
      return;
    }

    if (values.newPassword !== values.confirmPassword) {
      setLoading(false);
      message.error("Your new password and confirm password do not match!");
      return;
    }

    try {
      const res: AxiosResponse<{ activate_token: string }> = await defaultAxios.put(
        MERCHANT_ONBOARD_VERIFY(merchantOnboardId),
        values
      );
      if (res?.data.activate_token) {
        message.success("Verify OTP successfully");
        onSuccess(res.data.activate_token);
      }

      clearUserData();
      navigate(routers.LOGIN);
    } catch (error: any) {
      message.error(error.response.data.error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) {
      const emailArr = email.split("@");

      const repeatCount = emailArr[0].length - 3 > 0 ? emailArr[0].length - 3 : 0;
      const hiddenEmail = `${emailArr[0].slice(0, 3)} ${"*".repeat(repeatCount)}@${emailArr[1]}`;

      setHiddenEmail(hiddenEmail);
    }
  }, [email]);

  return (
    <CustomModal open={open} onCancel={() => setOpen(false)} title={"Sign up"} maskClosable={false}>
      <Form onFinish={onFinish} form={form} className={styles.content} layout="vertical">
        <div className={styles.description}>
          Welcome {name && `, ${name}`}
          <br /> Please enter the OTP we have sent to <br />
          {hiddenEmail}
        </div>
        <Form.Item
          name="otp"
          rules={[
            { required: true, message: "Please input your code!" },
            {
              min: 6,
              message: "OTP must be 6 digits",
            },
          ]}
        >
          <InputOtp
            setOtp={value => {
              form.setFieldsValue({ otp: value });
            }}
          />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label={<span className={styles.label}>New Password</span>}
          hasFeedback
          style={{ color: "white" }}
          validateTrigger={["onBlur", "onSubmit"]}
          rules={[
            { required: true, message: "Please input your new Password!" },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
              message:
                "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.",
            },
          ]}
        >
          <Input.Password placeholder="New Password" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={<span className={styles.label}>Confirm Password</span>}
          validateTrigger={["onBlur", "onSubmit"]}
          rules={[
            { required: true, message: "Please input your new Password!" },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
              message: "Password must match with new password",
            },
          ]}
        >
          <Input.Password placeholder="Confirm Password" autoComplete="confirm-password" />
        </Form.Item>

        <div className={styles.footer}>
          <PrimaryButton type="submit" disabled={loading}>
            Confirm
          </PrimaryButton>
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              requestVerifyOTP(merchantOnboardId);
            }}
            style={{
              marginTop: "16px",
              background: "none",
              border: "none",
              textDecoration: "underline",
            }}
          >
            <span style={{ textDecoration: "underline", cursor: "pointer" }} className={styles.label}>
              Resend OTP
            </span>
          </button>
        </div>
      </Form>
    </CustomModal>
  );
};
export default VerifyOTP;
